/**
 * Centralizes the localStorage keys the clubhouse uses to cache per-golfer data on
 * the device. Everything is namespaced under a single prefix so it can all be wiped
 * in one pass when the golfer signs out — otherwise the next account signing in on a
 * shared machine would see the previous golfer's cached profile and saved courses.
 */
const STORAGE_PREFIX = 'caddiesight.';

/** Cached copy of the signed-in golfer's profile (fast first paint / offline fallback). */
export const PROFILE_STORAGE_KEY = `${STORAGE_PREFIX}profile`;

/**
 * The promo campaign a golfer arrived through (scanned a QR code or opened a shared link). It is
 * how they got here, not per-golfer data, so it deliberately sits outside the `caddiesight.`
 * prefix: signing out to switch to another account mid-signup must not lose the offer.
 */
export const PROMO_CAMPAIGN_STORAGE_KEY = 'caddiesight_promo_campaign';

/**
 * Saved-course cache key, namespaced per golfer id so accounts sharing a device never
 * read or overwrite each other's saved-course list.
 */
export const coursesStorageKey = (userId: string): string => `${STORAGE_PREFIX}courses.${userId}`;

/**
 * Removes every cached CaddieSight value from localStorage. Called on logout so the
 * next account signing in on this device starts from a clean slate and never sees the
 * previous golfer's data.
 */
export function clearAppCache(): void {
  try {
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (key && key.startsWith(STORAGE_PREFIX)) keys.push(key);
    }
    keys.forEach((key) => localStorage.removeItem(key));
  } catch {
    // ignore private-mode / unavailable storage
  }
}
